import React, { useState, useMemo, useEffect } from 'react';
import {
  Droplets, X,
  Pencil, Check, Building2, Settings2,
  Loader2, RotateCcw, AlertTriangle, Info,
} from 'lucide-react';

const DIALOG_Z = 1400;


// ─── Rate Settings Modal ──────────────────────────────────────────────────────
export const RateSettingsModal = ({ onClose, onSave, globalRate, apartments, palette, Colors }) => {
  const [mode,    setMode]    = useState('global');
  const [newRate, setNewRate] = useState('');
  const [aptId,   setAptId]   = useState('');
  const [catId,   setCatId]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;
  const P   = Colors.primary.main;

  const aptCategories = useMemo(() => {
    if (!aptId) return [];
    const apt = apartments.find(a => String(a.id) === String(aptId));
    return apt ? apt.categories : [];
  }, [aptId, apartments]);

  useEffect(() => { setCatId(''); }, [aptId]);

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: `1px solid ${bdr}`, backgroundColor: bg.secondary,
    color: txt.primary, fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: '0.75rem', fontWeight: 700,
    color: txt.secondary, marginBottom: 6,
    textTransform: 'uppercase', letterSpacing: '0.05em',
  };

  const canSave = newRate && Number(newRate) > 0 &&
    (mode === 'global' || (aptId && catId));

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true); setError('');
    try {
      const payload = mode === 'global'
        ? { mode: 'global', rate: Number(newRate) }
        : { mode: 'category', apartmentId: Number(aptId), categoryId: Number(catId), rate: Number(newRate) };
      await onSave(payload);
      onClose();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: DIALOG_Z,
      backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        width: '100%', maxWidth: 520, borderRadius: 16,
        backgroundColor: bg.primary, border: `1px solid ${bdr}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', borderBottom: `1px solid ${bdr}`,
          background: `linear-gradient(135deg, ${P}18 0%, transparent 100%)`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: `${P}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Settings2 size={18} color={P} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: txt.primary }}>Water Rate Settings</div>
              <div style={{ fontSize: '0.75rem', color: txt.secondary }}>
                Current global rate: <strong style={{ color: P }}>KES {globalRate}/m³</strong>
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ color: txt.muted, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 8, padding: 4, backgroundColor: bg.secondary, borderRadius: 10, border: `1px solid ${bdr}` }}>
            {[
              { key: 'global',   label: 'Global Rate',         icon: <Droplets size={14} /> },
              { key: 'category', label: 'By Apartment / Type', icon: <Building2 size={14} /> },
            ].map(opt => (
              <button key={opt.key} onClick={() => setMode(opt.key)} style={{
                flex: 1, padding: '9px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                fontWeight: 600, fontSize: '0.8rem',
                backgroundColor: mode === opt.key ? P : 'transparent',
                color: mode === opt.key ? '#fff' : txt.secondary, transition: 'all 0.2s',
              }}>
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>

          {/* Apartment + category selectors */}
          {mode === 'category' && (
            <>
              <div>
                <label style={labelStyle}>Apartment</label>
                <select value={aptId} onChange={e => setAptId(e.target.value)} style={inputStyle}>
                  <option value="">Select apartment…</option>
                  {apartments.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Unit Category</label>
                <select value={catId} onChange={e => setCatId(e.target.value)} style={inputStyle} disabled={!aptId}>
                  <option value="">Select category…</option>
                  {aptCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.name} — current: KES {c.rate}/m³</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Rate input */}
          <div>
            <label style={labelStyle}>New Rate (KES / m³)</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: txt.muted, fontSize: '0.85rem', fontWeight: 600 }}>KES</span>
              <input type="number" min="1" value={newRate} onChange={e => setNewRate(e.target.value)}
                placeholder="e.g. 85" style={{ ...inputStyle, paddingLeft: 48 }} />
            </div>
            <p style={{ fontSize: '0.75rem', color: txt.muted, marginTop: 8 }}>
              {mode === 'global'
                ? 'Applies to all units across all apartments. Takes effect on the next bill run.'
                : 'Applies to selected apartment + category only. Takes effect on the next bill run.'}
            </p>
          </div>

          {error && (
            <div style={{ borderRadius: 8, padding: '10px 14px', backgroundColor: `${Colors.status?.error || '#EF4444'}15`, color: Colors.status?.error || '#EF4444', fontSize: '0.8rem' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${bdr}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: `1px solid ${bdr}`, backgroundColor: 'transparent', color: txt.secondary, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={!canSave || saving}
            style={{ padding: '9px 20px', borderRadius: 8, border: 'none', backgroundColor: canSave && !saving ? P : `${P}55`, color: '#fff', fontWeight: 600, fontSize: '0.875rem', cursor: canSave && !saving ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 6 }}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save Rate
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── Undo Dialog ──────────────────────────────────────────────────────────────
export const UndoDialog = ({ unit, onClose, onConfirm, palette, Colors }) => {
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;
  const P   = Colors.primary.main;
  const WARN = Colors.status?.warning || '#F59E0B';
  const ERR  = Colors.status?.error   || '#EF4444';

  const [undoing, setUndoing] = useState(false);
  const [error,   setError]   = useState('');

  const handleConfirm = async () => {
    setUndoing(true); setError('');
    try {
      await onConfirm(unit.unitId);
      onClose();
    } catch (e) {
      setError(e.message);
      setUndoing(false);
    }
  };

  const billAmount = unit.billAmount ?? 0;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: DIALOG_Z,
      backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        width: '100%', maxWidth: 480, borderRadius: 16,
        backgroundColor: bg.primary, border: `1px solid ${WARN}40`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', borderBottom: `1px solid ${bdr}`,
          background: `linear-gradient(135deg, ${WARN}14 0%, transparent 100%)`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: `${WARN}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <RotateCcw size={17} color={WARN} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: txt.primary }}>Undo Recording — Unit {unit.unit}</div>
              <div style={{ fontSize: '0.75rem', color: txt.secondary }}>{unit.tenant} · {unit.apartment}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ color: txt.muted, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Reading summary */}
          <div style={{ borderRadius: 10, border: `1px solid ${bdr}`, backgroundColor: bg.secondary, overflow: 'hidden' }}>
            <div style={{ padding: '8px 14px', borderBottom: `1px solid ${bdr}`, backgroundColor: `${WARN}0d` }}>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: WARN }}>
                Recording to be undone
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '14px 16px', gap: 8 }}>
              {[
                { label: 'Prev Reading', value: `${Number(unit.prevReading ?? 0).toLocaleString()} m³` },
                { label: 'Curr Reading', value: `${Number(unit.currReading ?? 0).toLocaleString()} m³` },
                { label: 'Bill Amount',  value: `KES ${Number(billAmount).toLocaleString()}`, bold: true },
              ].map(item => (
                <div key={item.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: item.bold ? 800 : 700, color: item.bold ? WARN : txt.primary }}>
                    {item.value}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: txt.muted, marginTop: 2, fontWeight: 500 }}>
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What happens */}
          <div style={{ borderRadius: 8, backgroundColor: `${WARN}0d`, border: `1px solid ${WARN}30`, padding: '12px 14px', fontSize: '0.8rem', color: txt.secondary, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, color: WARN, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <AlertTriangle size={13} /> What this will do
            </div>
            <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <li>KES {Number(billAmount).toLocaleString()} will be <strong>added back</strong> to {unit.tenant}'s balance</li>
              <li>Water arrears will be reduced by the same amount</li>
              <li>Current reading will reset to equal the previous reading</li>
              <li>A reversal transaction will be created for the audit trail</li>
            </ul>
          </div>

          {/* After-undo hint */}
          <div style={{ borderRadius: 8, backgroundColor: `${P}0d`, border: `1px solid ${P}25`, padding: '10px 14px', fontSize: '0.75rem', color: txt.secondary, lineHeight: 1.55, display: 'flex', alignItems: 'flex-start', gap: 7 }}>
            <Info size={13} style={{ color: P, flexShrink: 0, marginTop: 1 }} />
            <span>
              After completing the undo, click <strong style={{ color: P }}>Edit</strong> on the same row
              to enter the corrected reading.
            </span>
          </div>

          {error && (
            <div style={{ borderRadius: 8, padding: '10px 14px', backgroundColor: `${ERR}15`, color: ERR, fontSize: '0.8rem' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${bdr}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: `1px solid ${bdr}`, backgroundColor: 'transparent', color: txt.secondary, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={undoing}
            style={{
              padding: '9px 20px', borderRadius: 8, border: 'none',
              backgroundColor: undoing ? `${WARN}55` : WARN,
              color: '#fff', fontWeight: 600, fontSize: '0.875rem',
              cursor: undoing ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            {undoing ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
            Complete Undo
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── Edit Unit Modal ──────────────────────────────────────────────────────────
export const EditUnitModal = ({ unit, onClose, onSave, palette, Colors }) => {
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;
  const P   = Colors.primary.main;
  const ERR  = Colors.status?.error   || '#EF4444';
  const WARN = Colors.status?.warning || '#F59E0B';
  const OK   = Colors.status?.success || '#10B981';

  const isVacant = unit.tenant === 'Vacant';

  // After undo, currReading === prevReading — edit unlocks
  const isUndone = !isVacant && Number(unit.currReading) === Number(unit.prevReading);
  // Locked means occupied AND not undone yet
  const isLocked = !isVacant && !isUndone;

  const [prevReading,      setPrevReading]      = useState(String(unit.prevReading ?? ''));
  const [currReading,      setCurrReading]      = useState(String(unit.currReading ?? ''));
  const [rate,             setRate]             = useState(String(unit.rate ?? ''));
  const [imposeOnBalance,  setImposeOnBalance]  = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [error,            setError]            = useState('');

  const liveConsumption = Math.max(0, Number(currReading) - Number(prevReading));
  const liveBill        = liveConsumption * Number(rate);
  const consumptionChanged = liveConsumption !== 0;
  const rateChanged        = Number(rate) !== (unit.rate ?? 0);

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const payload = isVacant
        ? { currReading: Number(currReading), rate: Number(rate) }
        : {
            prevReading:     Number(prevReading),
            currReading:     Number(currReading),
            rate:            Number(rate),
            imposeOnBalance,
          };
      await onSave(unit.unitId, payload);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const inputStyle = (highlight) => ({
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: `1px solid ${highlight ? P : bdr}`,
    backgroundColor: bg.tertiary,
    color: txt.primary, fontSize: '0.9rem', outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 0.15s',
  });
  const labelStyle = {
    display: 'block', fontSize: '0.72rem', fontWeight: 700,
    color: txt.secondary, marginBottom: 5,
    textTransform: 'uppercase', letterSpacing: '0.05em',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: DIALOG_Z,
      backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        width: '100%', maxWidth: 600, borderRadius: 16,
        backgroundColor: bg.primary, border: `1px solid ${bdr}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', borderBottom: `1px solid ${bdr}`,
          background: `linear-gradient(135deg, ${P}18 0%, transparent 100%)`,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: `${P}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Pencil size={16} color={P} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: txt.primary }}>
                {isVacant ? 'Edit Unit' : 'Correct Recording'} — Unit {unit.unit}
              </div>
              <div style={{ fontSize: '0.75rem', color: txt.secondary }}>{unit.tenant} · {unit.apartment}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ color: txt.muted, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* LOCKED state — occupied and not undone yet */}
          {isLocked && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Current reading summary */}
              <div style={{ borderRadius: 10, border: `1px solid ${bdr}`, backgroundColor: bg.secondary, overflow: 'hidden' }}>
                <div style={{ padding: '8px 14px', borderBottom: `1px solid ${bdr}`, backgroundColor: `${P}0d` }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: P }}>
                    Current recorded reading
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '14px 16px', gap: 8 }}>
                  {[
                    { label: 'Prev Reading', value: `${Number(unit.prevReading ?? 0).toLocaleString()} m³` },
                    { label: 'Curr Reading', value: `${Number(unit.currReading ?? 0).toLocaleString()} m³` },
                    { label: 'Bill Amount',  value: `KES ${Number(unit.billAmount ?? 0).toLocaleString()}` },
                  ].map(item => (
                    <div key={item.label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 700, color: txt.primary }}>{item.value}</div>
                      <div style={{ fontSize: '0.68rem', color: txt.muted, marginTop: 2 }}>{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lock explanation */}
              <div style={{ borderRadius: 8, backgroundColor: `${WARN}0d`, border: `1px solid ${WARN}30`, padding: '14px 16px', lineHeight: 1.65 }}>
                <div style={{ fontWeight: 700, color: WARN, fontSize: '0.85rem', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <AlertTriangle size={14} /> Editing is locked
                </div>
                <div style={{ fontSize: '0.8rem', color: txt.secondary, marginBottom: 10 }}>
                  A recording has already been applied for this unit. To make a correction:
                </div>
                <ol style={{ margin: 0, paddingLeft: 18, fontSize: '0.8rem', color: txt.secondary, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <li>Close this dialog</li>
                  <li>Click <strong style={{ color: WARN }}>Undo</strong> on the same row</li>
                  <li>Complete the undo to reverse the recorded bill</li>
                  <li>Click <strong style={{ color: P }}>Edit</strong> again to enter the corrected reading</li>
                </ol>
              </div>
            </div>
          )}

          {/* UNLOCKED state — vacant or undone occupied */}
          {!isLocked && (
            <>
              {/* Live bill preview — occupied only */}
              {!isVacant && (
                <div style={{ borderRadius: 10, border: `1px solid ${bdr}`, backgroundColor: bg.secondary, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 16px', borderBottom: `1px solid ${bdr}`, backgroundColor: `${P}0d` }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: P }}>
                      Correction Preview
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '14px 16px', gap: 8 }}>
                    {[
                      { label: 'Consumption', value: `${liveConsumption} m³`,           changed: consumptionChanged },
                      { label: 'Bill Amount', value: `KES ${liveBill.toLocaleString()}`, changed: consumptionChanged || rateChanged },
                    ].map(item => (
                      <div key={item.label} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.92rem', fontWeight: 800, color: item.changed ? P : txt.primary, transition: 'color 0.2s' }}>
                          {item.value}
                        </div>
                        <div style={{ fontSize: '0.68rem', color: txt.muted, marginTop: 2, fontWeight: 500 }}>
                          {item.label}{item.changed && <span style={{ marginLeft: 4, color: P }}>●</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info note */}
              <div style={{ borderRadius: 8, backgroundColor: `${OK}0d`, border: `1px solid ${OK}25`, padding: '10px 14px', fontSize: '0.75rem', color: txt.secondary, lineHeight: 1.55, display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                <Info size={13} style={{ color: OK, flexShrink: 0, marginTop: 1 }} />
                <span>
                  {isVacant
                    ? <><strong style={{ color: OK }}>Vacant unit.</strong> Updates the meter reading and rate only. No tenant balance is affected.</>
                    : <><strong style={{ color: OK }}>Ready to correct.</strong> Enter the accurate readings below. Previous recording has been undone.</>
                  }
                </span>
              </div>

              {/* Previous Reading */}
              {!isVacant && (
                <div>
                  <label style={labelStyle}>Previous Reading (m³)</label>
                  <input
                    type="number" min="0" value={prevReading}
                    onChange={e => setPrevReading(e.target.value)}
                    style={inputStyle(false)}
                  />
                </div>
              )}

              {/* Current Reading */}
              <div>
                <label style={labelStyle}>Current Reading (m³)</label>
                <input
                  type="number" min="0" value={currReading}
                  onChange={e => setCurrReading(e.target.value)}
                  style={inputStyle(consumptionChanged)}
                />
                {!isVacant && consumptionChanged && (
                  <p style={{ fontSize: '0.71rem', color: P, marginTop: 4, fontWeight: 600 }}>
                    → {liveConsumption} m³ consumption this correction
                  </p>
                )}
              </div>

              {/* Rate */}
              <div>
                <label style={labelStyle}>Charge Rate (KES / m³)</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: txt.muted, fontSize: '0.85rem', fontWeight: 600 }}>KES</span>
                  <input
                    type="number" min="0" value={rate}
                    onChange={e => setRate(e.target.value)}
                    style={{ ...inputStyle(rateChanged), paddingLeft: 48 }}
                  />
                </div>
                <p style={{ fontSize: '0.71rem', color: txt.muted, marginTop: 4 }}>
                  Per-unit water rate for this {isVacant ? 'unit' : 'tenant'}
                </p>
              </div>

              {/* Impose on balance checkbox — occupied only */}
              {!isVacant && (
                <div
                  onClick={() => setImposeOnBalance(prev => !prev)}
                  style={{
                    borderRadius: 10, border: `1px solid ${imposeOnBalance ? P : bdr}`,
                    backgroundColor: imposeOnBalance ? `${P}0a` : bg.secondary,
                    padding: '12px 14px', cursor: 'pointer',
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    transition: 'all 0.15s',
                  }}>
                  {/* Checkbox */}
                  <div style={{
                    width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1,
                    border: `2px solid ${imposeOnBalance ? P : bdr}`,
                    backgroundColor: imposeOnBalance ? P : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}>
                    {imposeOnBalance && <Check size={11} color="#fff" strokeWidth={3} />}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: imposeOnBalance ? P : txt.primary }}>
                      Impose bill amount on tenant balance
                    </div>
                    <div style={{ fontSize: '0.74rem', color: txt.secondary, marginTop: 3, lineHeight: 1.5 }}>
                      {imposeOnBalance
                        ? 'The corrected bill will be charged as a new water reading and applied to the tenant\'s balance.'
                        : 'Only the reading values will be updated. The tenant\'s balance will not be affected.'
                      }
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {error && (
            <div style={{ borderRadius: 8, padding: '10px 14px', backgroundColor: `${ERR}15`, color: ERR, fontSize: '0.8rem' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${bdr}`, display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: `1px solid ${bdr}`, backgroundColor: 'transparent', color: txt.secondary, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
            {isLocked ? 'Close' : 'Cancel'}
          </button>
          {!isLocked && (
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '9px 20px', borderRadius: 8, border: 'none', backgroundColor: saving ? `${P}55` : P, color: '#fff', fontWeight: 600, fontSize: '0.875rem', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              Save Correction
            </button>
          )}
        </div>
      </div>
    </div>
  );
};